import { ArrowLeft, Download, Receipt } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourse, getTransaction } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { formatCurrency } from '../../utils/currency';
import { formatDate, formatDateTime } from '../../utils/date';

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatReceiptNumber(transaction) {
  return transaction?.receiptNumber || `SF-${String(transaction?.id || '').slice(0, 8).toUpperCase() || '00000000'}`;
}

function formatMethod(value) {
  const text = String(value || 'cash').replace(/[_-]+/g, ' ').trim();
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : 'Cash';
}

export function SchoolFeeReceiptPage() {
  const { transactionId } = useParams();
  const { activeTenantId, activeTenant } = useTenant();
  const currency = activeTenant?.settings?.currency || 'KES';
  const receiptRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [course, setCourse] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    if (!activeTenantId || !transactionId) {
      setTransaction(null);
      setCourse(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const nextTransaction = await getTransaction(activeTenantId, transactionId);
      setTransaction(nextTransaction);
      if (nextTransaction?.courseId) {
        setCourse(await getCourse(activeTenantId, nextTransaction.courseId));
      } else {
        setCourse(null);
      }
    } catch (error) {
      console.error('Unable to load receipt', error);
      setTransaction(null);
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId, transactionId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const receiptNumber = useMemo(() => formatReceiptNumber(transaction), [transaction]);
  const paymentDate = toDate(transaction?.paymentDate) || toDate(transaction?.createdAt);
  const amount = Number(transaction?.amount || 0);
  const schoolName = activeTenant?.name || 'Academy';
  const receiptTitle = transaction?.category === 'school_fee' ? 'School Fee Receipt' : 'Payment Receipt';

  async function handleDownloadPdf() {
    if (!receiptRef.current || downloading) return;

    setDownloading(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: -window.scrollY,
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const maxWidth = pageWidth - margin * 2;
      const maxHeight = pageHeight - margin * 2;
      const scale = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
      const imgWidth = canvas.width * scale;
      const imgHeight = canvas.height * scale;
      const x = (pageWidth - imgWidth) / 2;
      const y = (pageHeight - imgHeight) / 2;

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, imgWidth, imgHeight);

      pdf.save(`${receiptNumber}.pdf`);
    } catch (error) {
      console.error('Unable to download receipt PDF', error);
    } finally {
      setDownloading(false);
    }
  }

  if (loading) return <LoadingState label="Loading receipt..." />;

  if (!transaction) {
    return (
      <div className="incomeReceiptPage">
        <EmptyState
          title="Receipt not found."
          message="The payment record may have been removed or the receipt link is incomplete."
          actionLabel="Back to School Fee Income"
          to="/dashboard/finance/school-fee-income"
          icon={Receipt}
        />
      </div>
    );
  }

  return (
    <div className="incomeReceiptPage">
      <div className="incomeReceiptTopActions no-print">
        <Link className="viewBtn4" to="/dashboard/finance/school-fee-income">
          <ArrowLeft size={16} /> Back
        </Link>
        <Button icon={Download} onClick={handleDownloadPdf} disabled={downloading}>
          {downloading ? 'Preparing PDF...' : 'Download PDF'}
        </Button>
      </div>

      <div className="incomeReceiptShell">
        <section className="incomeReceiptPaper" ref={receiptRef}>
          <div className="incomeReceiptPrintArea">
            <div className="incomeReceiptHeader">
              <div className="incomeReceiptBrand">
                <div className="incomeReceiptMark" aria-hidden="true">
                  <Receipt size={20} />
                </div>
                <div>
                  <p className="incomeReceiptKicker">{schoolName}</p>
                  <h1>{receiptTitle}</h1>
                  <p className="incomeReceiptSubtext">Official confirmation of collected school fees</p>
                </div>
              </div>

              <div className="incomeReceiptHeaderMeta">
                <div>
                  <span>Receipt No.</span>
                  <strong>{receiptNumber}</strong>
                </div>
                <div>
                  <span>Date Paid</span>
                  <strong>{paymentDate ? formatDate(paymentDate) : '—'}</strong>
                </div>
              </div>
            </div>

            <div className="incomeReceiptRule" />

            <div className="incomeReceiptAmountBar">
              <div>
                <span>Total received</span>
                <strong>{formatCurrency(amount, currency)}</strong>
              </div>
              <div className="incomeReceiptBadge">Paid in full</div>
            </div>

            <div className="incomeReceiptRule" />

            <div className="incomeReceiptMetaGrid">
              <div className="incomeReceiptMetaRow">
                <span>Student</span>
                <strong>{transaction.studentName || 'Student record unavailable'}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Course</span>
                <strong>{transaction.courseName || course?.name || '—'}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Phone</span>
                <strong>{transaction.studentPhone || '—'}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Email</span>
                <strong>{transaction.studentEmail || '—'}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Payment method</span>
                <strong>{formatMethod(transaction.paymentMethod)}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Recorded at</span>
                <strong>{formatDateTime(transaction.createdAt)}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Reference</span>
                <strong>{transaction.reference || receiptNumber}</strong>
              </div>
              <div className="incomeReceiptMetaRow">
                <span>Student ID</span>
                <strong>{transaction.studentId || '—'}</strong>
              </div>
            </div>

            <div className="incomeReceiptRule" />

            <div className="incomeReceiptSummaryLine">
              <div>
                <span>Received from</span>
                <strong>{transaction.studentName || '—'}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>Completed</strong>
              </div>
            </div>

            <div className="incomeReceiptFooter">
              <p>This is a computer-generated receipt and does not require a signature.</p>
              <p>Keep this receipt for your records and fee reconciliation.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
